/* eslint-disable react/display-name */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Progress,
  Text,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
} from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  HistoricalAssetVerificationDataGQLType,
  GetAssetVerificationReceivableHistoryQueryGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { AssetVerificationStatusSelector } from './AssetVerificationReceivablesTable';
import {
  AssetVerificationSummaryCounts,
  LenderVerificationStatus,
} from './AssetVerificationReceivablesTable/types';
import { colors } from 'packages/common-frontend/colors';
import { BasicTable } from 'packages/common-frontend/components/TanStack';
import { numberToPercentageLabel } from 'packages/common-frontend/utils';

type ReceivablesVerificationTableColumnDef = ColumnDef<any, any> & {
  meta: {
    alwaysVisible: boolean;
    verificationStatus?: LenderVerificationStatus;
  };
};

type ReceivableHistoryRange = 'all' | 'past7Days' | 'past30Days';

const TableHeader = (name: string) => {
  return (
    <Text color="black" fontSize="16px" padding="8px 0px">
      {name}
    </Text>
  );
};

type HistoricalAssetVerificationTableData = Omit<
  HistoricalAssetVerificationDataGQLType,
  'assignedAtDate'
> & {
  assignedAtIdentifier: string;
};

type AssetVerificationReceivablesHistoryTableProps = {
  data: HistoricalAssetVerificationDataGQLType[];
  selectedVerificationStatus: LenderVerificationStatus;
  selectedReceivableHistoryRange: ReceivableHistoryRange;
};
const AssetVerificationReceivablesHistoryTable = ({
  data,
  selectedVerificationStatus,
  selectedReceivableHistoryRange,
}: AssetVerificationReceivablesHistoryTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const [tableData, setTableData] = useState<
    HistoricalAssetVerificationTableData[]
  >([]);

  const columns: ReceivablesVerificationTableColumnDef[] = [
    {
      id: 'assignedAtIdentifier',
      accessorKey: 'assignedAtIdentifier',
      header: () => TableHeader('Assigned At'),
      cell: ({ getValue }) => {
        const assignedAtIdentifier: string = getValue();
        return (
          <Text
            fontWeight={
              assignedAtIdentifier.startsWith('Before') ? '600' : '400'
            }
          >
            {assignedAtIdentifier}
          </Text>
        );
      },
      size: 110,
      meta: {
        alwaysVisible: true,
      },
      enableSorting: selectedReceivableHistoryRange === 'all',
    },
    {
      id: 'totalUntestedAssignedAtExposure',
      accessorKey: 'totalUntestedAssignedAtExposure',
      header: () => TableHeader('Untested Exposure'),
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      meta: {
        alwaysVisible: false,
        verificationStatus: 'untested',
      },
    },
    {
      id: 'totalUnverifiedFailureAssignedAtExposure',
      accessorKey: 'totalUnverifiedFailureAssignedAtExposure',
      header: () => TableHeader('Under Finley Review Exposure'),
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      meta: {
        alwaysVisible: false,
        verificationStatus: 'finleyReview',
      },
    },
    {
      id: 'totalVerifiedFailureAssignedAtExposure',
      accessorKey: 'totalVerifiedFailureAssignedAtExposure',
      header: () => TableHeader('Failed Verification Exposure'),
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      meta: {
        alwaysVisible: false,
        verificationStatus: 'failed',
      },
    },
    {
      id: 'totalVerifiedPassAssignedAtExposure',
      accessorKey: 'totalVerifiedPassAssignedAtExposure',
      header: () => TableHeader('Passed Verification Exposure'),
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      meta: {
        alwaysVisible: false,
        verificationStatus: 'passed',
      },
    },
    {
      id: 'totalAssignedAtExposure',
      accessorKey: 'totalAssignedAtExposure',
      header: () => TableHeader('Total Exposure'),
      cell: ({ getValue }) =>
        commonUtils.numberToCurrencyWithSymbol({
          number: Number(getValue()),
        }),
      meta: {
        alwaysVisible: true,
      },
    },
    {
      id: 'percentUntestedAssignedAtExposure',
      header: () => TableHeader('% Untested Exposure'),
      cell: ({ row }) => {
        return numberToPercentageLabel(
          commonUtils.divideByOrZero(
            row.original.totalUntestedAssignedAtExposure,
            row.original.totalAssignedAtExposure,
          ),
        );
      },
      meta: {
        alwaysVisible: false,
        verificationStatus: 'untested',
      },
    },
    {
      id: 'percentUnverifiedFailureAssignedAtExposure',
      header: () => TableHeader('% Under Finley Review Exposure'),
      cell: ({ row }) => {
        return numberToPercentageLabel(
          commonUtils.divideByOrZero(
            row.original.totalUnverifiedFailureAssignedAtExposure,
            row.original.totalAssignedAtExposure,
          ),
        );
      },
      meta: {
        alwaysVisible: false,
        verificationStatus: 'finleyReview',
      },
    },
    {
      id: 'percentVerifiedPassAssignedAtExposure',
      header: () => TableHeader('% Passed Verification Exposure'),
      cell: ({ row }) => {
        return numberToPercentageLabel(
          commonUtils.divideByOrZero(
            row.original.totalVerifiedPassAssignedAtExposure,
            row.original.totalAssignedAtExposure,
          ),
        );
      },
      meta: {
        alwaysVisible: false,
        verificationStatus: 'passed',
      },
    },
    {
      id: 'percentVerifiedFailureAssignedAtExposure',
      header: () => TableHeader('% Failed Verification Exposure'),
      cell: ({ row }) => {
        return numberToPercentageLabel(
          commonUtils.divideByOrZero(
            row.original.totalVerifiedFailureAssignedAtExposure,
            row.original.totalAssignedAtExposure,
          ),
        );
      },
      meta: {
        alwaysVisible: false,
        verificationStatus: 'failed',
      },
    },
    {
      id: 'unverifiedFailureReceivableCount',
      accessorKey: 'unverifiedFailureReceivableCount',
      header: () => TableHeader('Under Finley Review Receivable Count'),
      size: 150,
      meta: {
        alwaysVisible: false,
        verificationStatus: 'finleyReview',
      },
    },
    {
      id: 'verifiedPassReceivableCount',
      accessorKey: 'verifiedPassReceivableCount',
      header: () => TableHeader('Passed Verification Receivable Count'),
      size: 150,
      meta: {
        alwaysVisible: false,
        verificationStatus: 'passed',
      },
    },
    {
      id: 'untestedReceivableCount',
      accessorKey: 'untestedReceivableCount',
      header: () => TableHeader('Untested Receivable Count'),
      size: 150,
      meta: {
        alwaysVisible: false,
        verificationStatus: 'untested',
      },
    },
    {
      id: 'verifiedFailureReceivableCount',
      accessorKey: 'verifiedFailureReceivableCount',
      header: () => TableHeader('Failed Verification Receivable Count'),
      size: 150,
      meta: {
        alwaysVisible: false,
        verificationStatus: 'failed',
      },
    },
    {
      id: 'totalReceivableCount',
      accessorKey: 'totalReceivableCount',
      header: () => TableHeader('Total Receivable Count'),
      size: 150,
      meta: {
        alwaysVisible: true,
      },
    },
  ];

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setColumnVisibility(
      columns.reduce((acc, columnDef) => {
        if (columnDef.id) {
          // eslint-disable-next-line no-param-reassign
          acc[columnDef.id] =
            columnDef.meta.alwaysVisible ||
            columnDef.meta.verificationStatus === selectedVerificationStatus;
        }
        return acc;
      }, {} as Record<string, boolean>),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVerificationStatus]);

  useEffect(() => {
    const sortedData = data
      .slice()
      .sort(
        (a, b) =>
          commonDate.fullDateStrToDate(b.assignedAtDate).getTime() -
          commonDate.fullDateStrToDate(a.assignedAtDate).getTime(),
      );
    if (selectedReceivableHistoryRange === 'all') {
      setTableData(
        sortedData.map((elem) => {
          return {
            ...elem,
            assignedAtIdentifier: commonDate.fullDateStrToHumanDateString(
              elem.assignedAtDate,
            ),
          };
        }),
      );
    } else {
      const dayRange = selectedReceivableHistoryRange === 'past7Days' ? 7 : 30;
      const dateLimit = commonDate.addDays(
        commonDate.currentDateStr(),
        -dayRange,
      );
      const cumulativeDataRow: HistoricalAssetVerificationTableData = {
        assignedAtIdentifier: `Before ${commonDate.fullDateStrToHumanDateString(
          dateLimit,
        )}`,
        totalAssignedAtExposure: 0,
        totalReceivableCount: 0,
        totalUntestedAssignedAtExposure: 0,
        totalUnverifiedFailureAssignedAtExposure: 0,
        totalVerifiedFailureAssignedAtExposure: 0,
        totalVerifiedPassAssignedAtExposure: 0,
        verifiedFailureReceivableCount: 0,
        verifiedPassReceivableCount: 0,
        untestedReceivableCount: 0,
        unverifiedFailureReceivableCount: 0,
      };
      const tableDataArr: HistoricalAssetVerificationTableData[] = [];
      sortedData.forEach((elem) => {
        if (elem.assignedAtDate < dateLimit) {
          cumulativeDataRow.totalAssignedAtExposure +=
            elem.totalAssignedAtExposure;
          cumulativeDataRow.totalUntestedAssignedAtExposure +=
            elem.totalUntestedAssignedAtExposure;
          cumulativeDataRow.totalUnverifiedFailureAssignedAtExposure +=
            elem.totalUnverifiedFailureAssignedAtExposure;
          cumulativeDataRow.totalVerifiedFailureAssignedAtExposure +=
            elem.totalVerifiedFailureAssignedAtExposure;
          cumulativeDataRow.totalVerifiedPassAssignedAtExposure +=
            elem.totalVerifiedPassAssignedAtExposure;
          cumulativeDataRow.totalReceivableCount += elem.totalReceivableCount;
          cumulativeDataRow.verifiedPassReceivableCount +=
            elem.verifiedPassReceivableCount;
          cumulativeDataRow.verifiedFailureReceivableCount +=
            elem.verifiedFailureReceivableCount;
          cumulativeDataRow.unverifiedFailureReceivableCount +=
            elem.unverifiedFailureReceivableCount;
          cumulativeDataRow.untestedReceivableCount +=
            elem.untestedReceivableCount;
        } else {
          tableDataArr.push({
            ...elem,
            assignedAtIdentifier: commonDate.fullDateStrToHumanDateString(
              elem.assignedAtDate,
            ),
          });
        }
      });
      tableDataArr.push(cumulativeDataRow);
      setTableData(tableDataArr);
    }
  }, [data, selectedReceivableHistoryRange]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return <BasicTable table={table} />;
};

const calculateSummaryCounts = (
  assetVerificationReceivableHistoryData:
    | GetAssetVerificationReceivableHistoryQueryGQLType
    | undefined,
): AssetVerificationSummaryCounts => {
  const summaryCounts: AssetVerificationSummaryCounts = {
    passed: 0,
    failed: 0,
    untested: 0,
    finleyReview: 0,
  };
  assetVerificationReceivableHistoryData?.assetVerificationReceivableHistory.forEach(
    (historicalDataSummary) => {
      summaryCounts.passed += historicalDataSummary.verifiedPassReceivableCount;
      summaryCounts.failed +=
        historicalDataSummary.verifiedFailureReceivableCount;
      summaryCounts.untested += historicalDataSummary.untestedReceivableCount;
      summaryCounts.finleyReview +=
        historicalDataSummary.unverifiedFailureReceivableCount;
    },
  );
  return summaryCounts;
};

type ReceivableHistoryRangeOptionProps = {
  selectedReceivableHistoryRange: ReceivableHistoryRange;
  setSelectedReceivableHistoryRange: React.Dispatch<
    React.SetStateAction<ReceivableHistoryRange>
  >;
  rangeOptionType: ReceivableHistoryRange;
};
const ReceivableHistoryRangeOption = ({
  selectedReceivableHistoryRange,
  setSelectedReceivableHistoryRange,
  rangeOptionType,
}: ReceivableHistoryRangeOptionProps) => {
  let optionText;
  switch (rangeOptionType) {
    case 'all':
      optionText = 'All Assigned Receivables';
      break;
    case 'past7Days':
      optionText = 'Past 7 Days';
      break;
    case 'past30Days':
      optionText = 'Past 30 Days';
      break;
    default:
      throw finleyAssertUnreachable(rangeOptionType);
  }

  const isSelected = rangeOptionType === selectedReceivableHistoryRange;

  return (
    <Box
      borderRadius="8px"
      padding="8px 16px"
      background={isSelected ? colors.aurora[400] : 'none'}
      ml="16px"
      color={isSelected ? 'white' : 'black'}
      border={isSelected ? 'none' : '1px'}
      borderColor={colors.gray[200]}
      cursor="pointer"
      onClick={() => setSelectedReceivableHistoryRange(rangeOptionType)}
    >
      {optionText}
    </Box>
  );
};

type ReceivableHistoryRangeOptionSelectorProps = {
  selectedReceivableHistoryRange: ReceivableHistoryRange;
  setSelectedReceivableHistoryRange: React.Dispatch<
    React.SetStateAction<ReceivableHistoryRange>
  >;
};
const ReceivableHistoryRangeOptionSelector = ({
  selectedReceivableHistoryRange,
  setSelectedReceivableHistoryRange,
}: ReceivableHistoryRangeOptionSelectorProps) => {
  return (
    <Flex justifyContent="flex-end">
      <ReceivableHistoryRangeOption
        selectedReceivableHistoryRange={selectedReceivableHistoryRange}
        setSelectedReceivableHistoryRange={setSelectedReceivableHistoryRange}
        rangeOptionType="past7Days"
      />
      <ReceivableHistoryRangeOption
        selectedReceivableHistoryRange={selectedReceivableHistoryRange}
        setSelectedReceivableHistoryRange={setSelectedReceivableHistoryRange}
        rangeOptionType="past30Days"
      />
      <ReceivableHistoryRangeOption
        selectedReceivableHistoryRange={selectedReceivableHistoryRange}
        setSelectedReceivableHistoryRange={setSelectedReceivableHistoryRange}
        rangeOptionType="all"
      />
    </Flex>
  );
};

type AssetVerificationReceivableHistoryProps = {
  assetVerificationReceivableHistoryData:
    | GetAssetVerificationReceivableHistoryQueryGQLType
    | undefined;
  isDataLoading: boolean;
};
export const AssetVerificationReceivableHistory = ({
  assetVerificationReceivableHistoryData,
  isDataLoading,
}: AssetVerificationReceivableHistoryProps) => {
  const [
    selectedVerificationStatus,
    setSelectedVerificationStatus,
  ] = React.useState<LenderVerificationStatus>('finleyReview');

  const [
    selectedReceivableHistoryRange,
    setSelectedReceivableHistoryRange,
  ] = React.useState<ReceivableHistoryRange>('past7Days');

  const summaryCounts = calculateSummaryCounts(
    assetVerificationReceivableHistoryData,
  );

  return (
    <Box
      borderRadius="8px"
      border={`1px solid ${colors.gray[200]}`}
      backgroundColor="white"
      padding="24px"
    >
      {isDataLoading && (
        <>
          <Alert status="info">Compiling historical information...</Alert>
          <Progress size="xs" isIndeterminate />
        </>
      )}
      {!isDataLoading && !assetVerificationReceivableHistoryData && (
        <>
          <Alert status="error">
            <AlertIcon />
            <AlertDescription>
              Error occurred while fetching historical asset verification data.
            </AlertDescription>
          </Alert>
        </>
      )}
      {assetVerificationReceivableHistoryData && (
        <Flex direction="column">
          <ReceivableHistoryRangeOptionSelector
            selectedReceivableHistoryRange={selectedReceivableHistoryRange}
            setSelectedReceivableHistoryRange={
              setSelectedReceivableHistoryRange
            }
          />
          <AssetVerificationStatusSelector
            selectedType={selectedVerificationStatus}
            setSelectedType={setSelectedVerificationStatus}
            summaryCounts={summaryCounts}
          />
          <Box>
            <AssetVerificationReceivablesHistoryTable
              data={
                assetVerificationReceivableHistoryData.assetVerificationReceivableHistory
              }
              selectedVerificationStatus={selectedVerificationStatus}
              selectedReceivableHistoryRange={selectedReceivableHistoryRange}
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};
